import { LayoutDashboard, Megaphone, BarChart3, CalendarDays, Settings, ShieldCheck, BookOpen, Mail } from 'lucide-preact';
import { useCallback } from 'preact/hooks';
import { LAYOUT_CONSTANTS } from '../constants/layout';
import { THEME_COLORS } from '../constants/theme';
import { useTheme } from '../hooks/useTheme';
import { CompanySelector } from './common/CompanySelector';
import { WorkspaceSelector } from './common/WorkspaceSelector';
import { useCompanyWorkspaceNav } from '@/hooks/useCompanyWorkspaceNav';
import { useUserProfile } from '@/hooks/useUserProfile';
import type { ClientWorkspaceSummaryViewDto } from '@/types/company';

import { Section } from '@/utils/routes'

interface LeftNavigationProps {
  activeSection: Section | string
  onSectionChange: (section: Section | string) => void
  isOpen?: boolean
  onToggle?: () => void
  clientCompanyId?: string
  onWorkspaceChange?: (workspace: ClientWorkspaceSummaryViewDto) => void
}

export function LeftNavigation({
  activeSection,
  onSectionChange,
  isOpen = true,
  onToggle,
  clientCompanyId,
  onWorkspaceChange
}: LeftNavigationProps) {
  const { resolvedTheme } = useTheme()
  const assetsBaseUrl = import.meta.env.VITE_ASSETS_BASE_URL

  const {
    companies,
    showCompanySelector,
    currentCompanyWorkspaces,
    currentWorkspaceId,
    handleCompanyChange: baseHandleCompanyChange,
    handleWorkspaceChange: baseHandleWorkspaceChange,
  } = useCompanyWorkspaceNav({ clientCompanyId, onWorkspaceChange })

  const { getCurrentCompanyRoleSet, getWorkspaceCapabilities } = useUserProfile()
  const isCompanyAdmin = getCurrentCompanyRoleSet()?.IsAdmin ?? false
  const workspaceCaps = clientCompanyId && currentWorkspaceId
    ? getWorkspaceCapabilities(clientCompanyId, currentWorkspaceId)
    : null
  const isWorkspaceAdmin = workspaceCaps?.canAdmin ?? isCompanyAdmin
  const canEdit = workspaceCaps?.canEdit ?? isCompanyAdmin

  // Use different logos based on theme
  const logoUrl = resolvedTheme === 'dark'
    ? `${assetsBaseUrl}/assets/white-orbital-x.png`
    : `${assetsBaseUrl}/assets/black-orbital-x.png`

  // Wrap handlers to close mobile menu after selection
  const handleCompanyChange = useCallback((companyId: string) => {
    baseHandleCompanyChange(companyId)
    if (onToggle && window.innerWidth < 1024) onToggle()
  }, [baseHandleCompanyChange, onToggle])

  const handleWorkspaceChange = useCallback((workspace: ClientWorkspaceSummaryViewDto) => {
    baseHandleWorkspaceChange(workspace)
    if (onToggle && window.innerWidth < 1024) onToggle()
  }, [baseHandleWorkspaceChange, onToggle])

  const mainNavigationItems = [
    { id: Section.Dashboard, label: 'Dashboard', icon: LayoutDashboard },
    { id: Section.Campaigns, label: 'Campaigns', icon: Megaphone },
    { id: Section.Emails, label: 'Emails', icon: Mail },
    { id: Section.Leads, label: 'Leads', icon: BarChart3 },
    { id: Section.Calendar, label: 'Calendar', icon: CalendarDays },
  ]

  // Configuration section items — KB visible to editors+, Settings admin-only
  const configurationItems = [
    ...(canEdit ? [{ id: Section.KnowledgeBase, label: 'Knowledge Base', icon: BookOpen }] : []),
    ...(isWorkspaceAdmin ? [
      { id: Section.Settings, label: 'Settings', icon: Settings },
    ] : []),
  ]

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onToggle}
        />
      )}

      {/* Navigation sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50
        w-64 
        bg-white dark:bg-slate-900 text-slate-900 dark:text-white border-r border-slate-200 dark:border-slate-700
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col h-screen
      `}>
        {/* Logo section */}
        <div
          className="p-4 flex items-center justify-center"
          style={{ height: `${LAYOUT_CONSTANTS.TOP_NAV_HEIGHT}px` }}
        >
          <div className="flex items-center">
            <img
              src={logoUrl}
              alt="OrbitalX"
              className="h-[40px] w-auto lg:h-[60px]"
              onError={(e) => {
                console.error('Failed to load logo from:', logoUrl)
                // Fallback to text if image fails to load
                e.currentTarget.style.display = 'none'
                e.currentTarget.nextElementSibling?.classList.remove('hidden')
              }}
            />
            {/* Fallback text logo */}
            <span className="hidden text-xl font-bold">OrbitalX</span>
          </div>
        </div>

        {/* Mobile-only: Company & Workspace selectors */}
        {clientCompanyId && (
          <div className="lg:hidden px-4 pb-3 space-y-2 border-b border-slate-200 dark:border-slate-700">
            {showCompanySelector && (
              <CompanySelector
                selectedCompanyId={clientCompanyId}
                onCompanyChange={handleCompanyChange}
                companies={companies}
              />
            )}
            {onWorkspaceChange && (
              <WorkspaceSelector
                selectedWorkspaceId={currentWorkspaceId || undefined}
                onWorkspaceChange={handleWorkspaceChange}
                workspaces={currentCompanyWorkspaces || null}
              />
            )}
          </div>
        )}

        {/* Navigation items */}
        <nav className="flex-1 overflow-y-auto p-4">
          <ul className="space-y-2">
            {mainNavigationItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => {
                    onSectionChange(item.id)
                    if (onToggle && window.innerWidth < 1024) {
                      onToggle()
                    }
                  }}
                  className={`
                    w-full flex items-center px-3 py-3 text-left rounded-lg transition-colors duration-200
                    ${activeSection === item.id
                      ? 'text-slate-900 dark:text-slate-100 border'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                    }
                  `}
                  style={{
                    backgroundColor: activeSection === item.id ? THEME_COLORS.BACKGROUNDS.LIGHT_SUBTLE : 'transparent',
                    borderColor: activeSection === item.id ? THEME_COLORS.BORDERS.LIGHT : 'transparent'
                  }}
                  title={item.label}
                >
                  <item.icon
                    className="mr-3 w-5 h-5 transition-colors duration-200"
                    style={{ color: activeSection === item.id ? THEME_COLORS.GREEN[500] : 'inherit' }}
                  />
                  <span
                    className="text-sm font-medium transition-colors duration-200"
                    style={{ color: activeSection === item.id ? THEME_COLORS.GREEN[600] : 'inherit' }}
                  >
                    {item.label}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {/* Configuration section — KB for editors+, Settings/Integrations for admins */}
          {configurationItems.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <h3 className="px-3 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">
                Configuration
              </h3>
              <ul className="space-y-2">
                {configurationItems.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => {
                        onSectionChange(item.id)
                        if (onToggle && window.innerWidth < 1024) {
                          onToggle()
                        }
                      }}
                      className={`
                        w-full flex items-center px-3 py-3 text-left rounded-lg transition-colors duration-200
                        ${activeSection === item.id
                          ? 'text-slate-900 dark:text-slate-100 border'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                        }
                      `}
                      style={{
                        backgroundColor: activeSection === item.id ? THEME_COLORS.BACKGROUNDS.LIGHT_SUBTLE : 'transparent',
                        borderColor: activeSection === item.id ? THEME_COLORS.BORDERS.LIGHT : 'transparent'
                      }}
                      title={item.label}
                    >
                      <item.icon
                        className="mr-3 w-5 h-5 transition-colors duration-200"
                        style={{ color: activeSection === item.id ? THEME_COLORS.GREEN[500] : 'inherit' }}
                      />
                      <span
                        className="text-sm font-medium transition-colors duration-200"
                        style={{ color: activeSection === item.id ? THEME_COLORS.GREEN[600] : 'inherit' }}
                      >
                        {item.label}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </nav>

        {/* Admin pinned to bottom — company admins only */}
        {isCompanyAdmin && <div className="flex-shrink-0 px-4 pb-2">
          <button
            onClick={() => {
              onSectionChange(Section.Admin)
              if (onToggle && window.innerWidth < 1024) {
                onToggle()
              }
            }}
            className={`
              w-full flex items-center px-3 py-3 text-left rounded-lg transition-colors duration-200
              ${activeSection === Section.Admin
                ? 'text-slate-900 dark:text-slate-100 border'
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
              }
            `}
            style={{
              backgroundColor: activeSection === Section.Admin ? THEME_COLORS.BACKGROUNDS.LIGHT_SUBTLE : 'transparent',
              borderColor: activeSection === Section.Admin ? THEME_COLORS.BORDERS.LIGHT : 'transparent'
            }}
            title="Admin"
          >
            <ShieldCheck
              className="mr-3 w-5 h-5 transition-colors duration-200"
              style={{ color: activeSection === Section.Admin ? THEME_COLORS.GREEN[500] : 'inherit' }}
            />
            <span
              className="text-sm font-medium transition-colors duration-200"
              style={{ color: activeSection === Section.Admin ? THEME_COLORS.GREEN[600] : 'inherit' }}
            >
              Admin
            </span>
          </button>
        </div>}

        {/* Footer with version */}
        <div className="flex-shrink-0 p-3 border-t border-slate-200 dark:border-slate-700">
          <div className="text-xs text-slate-500 dark:text-slate-400 text-center font-mono">
            v{import.meta.env.VITE_RELEASE || import.meta.env.VITE_APP_VERSION || '1.0.0'}
          </div>
        </div>
      </div>
    </>
  )
}
